/** The admin renders dates and numbers in one locale; `number.ts` shares it. */
export const ADMIN_LOCALE = 'en-GB';

const adminDateFormatter = new Intl.DateTimeFormat(ADMIN_LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const adminDateTimeFormatter = new Intl.DateTimeFormat(ADMIN_LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const toDate = (value: string | Date): Date | null => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
};

const toDateOnly = (value: string | Date): Date | null => {
  if (value instanceof Date) return toDate(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return toDate(value);
  const [, year, month, day] = match;
  return toDate(new Date(Number(year), Number(month) - 1, Number(day)));
};

export const formatDate = (value: string | Date | null | undefined, fallback = '—'): string => {
  if (!value) return fallback;
  const date = toDateOnly(value);
  return date ? adminDateFormatter.format(date) : fallback;
};

export const formatDateTime = (value: string | Date | null | undefined, fallback = '—'): string => {
  if (!value) return fallback;
  const date = toDate(value);
  return date ? adminDateTimeFormatter.format(date) : fallback;
};

/**
 * The design's compact date — `28 May · 13:17` (design-system.md: "compact dates
 * `28 May · 13:17`"). No year, middot between the day and the time.
 *
 * `formatFixtureRowDateTime` below is the same data with a comma, from before
 * the redesign; it folds into this one when the catalogue is rebuilt.
 */
const compactDateTimeFormatter = new Intl.DateTimeFormat(ADMIN_LOCALE, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export const formatCompactDateTime = (
  value: string | Date | null | undefined,
  fallback = '—',
): string => {
  if (!value) return fallback;
  const date = toDate(value);
  return date ? compactDateTimeFormatter.format(date).replace(', ', ' · ') : fallback;
};

/**
 * The clock half of the Contests list's two-line date cell — `13:17` under
 * `28 May 2026`.
 *
 * Its own formatter rather than a slice of `formatDateTime`'s output: the
 * prototype splits a pre-formatted string on its comma because its mock data is
 * already formatted, which is a property of the mock and not a thing to
 * reproduce against ISO timestamps.
 */
const timeOfDayFormatter = new Intl.DateTimeFormat(ADMIN_LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export const formatTimeOfDay = (value: string | Date | null | undefined, fallback = ''): string => {
  if (!value) return fallback;
  const date = toDate(value);
  return date ? timeOfDayFormatter.format(date) : fallback;
};

/** Compact kick-off time for catalogue fixture rows (e.g. "11 Jun, 20:00"). */
const fixtureRowDateTimeFormatter = new Intl.DateTimeFormat(ADMIN_LOCALE, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export const formatFixtureRowDateTime = (
  value: string | Date | null | undefined,
  fallback = '—',
): string => {
  if (!value) return fallback;
  const date = toDate(value);
  return date ? fixtureRowDateTimeFormatter.format(date) : fallback;
};

/**
 * One end of a calendar day in the operator's own timezone, as an instant — how
 * a `<input type="date">` value becomes a filter the catalogue endpoint can
 * take.
 *
 * `new Date('2026-06-20T00:00:00.000')` — no `Z` — is parsed as local time, and
 * that is the point: the operator picked a day in their calendar, not a UTC
 * window. Stamping it `Z` would drop a 20:00 kickoff for an operator in New
 * York, because that is `T00:00Z` the day after.
 *
 * `end` widens to the last millisecond of the day, so picking the same date in
 * both boxes means "that day" rather than "the instant midnight passed", which
 * would match nothing.
 *
 * Shared by the two screens that filter the catalogue by date — the Catalogue
 * itself and the Add Fixtures modal. A fixture must not appear on one and
 * vanish from the other for the same date, which it would the moment the two
 * converted differently.
 */
export const localDayBoundary = (day: string, edge: 'start' | 'end'): string | undefined =>
  day
    ? new Date(`${day}T${edge === 'start' ? '00:00:00.000' : '23:59:59.999'}`).toISOString()
    : undefined;

/**
 * A span of milliseconds as the handoff writes durations: `Xd Yh` / `Xh Ym` /
 * `Xm`, "whichever is most informative" (design-system.md §6 "Age chip", and
 * `computeRelative()` in the prototype).
 *
 * Floors at every step. A contest that entered its state 27 minutes and 59
 * seconds ago reads `27m`, never `28m` — an operator reading an age chip is
 * asking how long something has been true, and rounding up answers a question
 * they did not ask.
 *
 * A negative span floors to zero rather than rendering `-1m`. The chip's input
 * is `now - statusEnteredAt`, and a client clock a little behind the server's is
 * ordinary; the design has no state for "in the future" and does not need one.
 *
 * This replaced `formatDurationMs`, which rendered seconds — CAE-1541 asked for
 * one formatter in the design's shape, so the Contests list and the contest
 * header stopped showing a seconds component.
 */
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const formatDuration = (milliseconds: number): string => {
  // `Math.max(0, NaN)` is NaN, which falls through every branch below and puts
  // the literal word NaN on the screen. The chip's input is
  // `now - statusEnteredAt`, so a missing or unparseable API timestamp produces
  // exactly that; Infinity comes the same way. Both read as zero elapsed.
  const span = Number.isFinite(milliseconds) ? Math.max(0, milliseconds) : 0;

  if (span >= DAY) {
    return `${Math.floor(span / DAY)}d ${Math.floor((span % DAY) / HOUR)}h`;
  }

  if (span >= HOUR) {
    return `${Math.floor(span / HOUR)}h ${Math.floor((span % HOUR) / MINUTE)}m`;
  }

  return `${Math.floor(span / MINUTE)}m`;
};
